import { SiteHeader } from "@/components/site-header"

export default function ShipmentTripStatusesPage() {
  return (
    <>
      <SiteHeader title="Estados de Envíos y Viajes" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="max-w-5xl space-y-12">

          {/* ── Intro ── */}
          <section>
            <h2 className="text-base font-semibold mb-2">Descripción general</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Un <strong>Envío</strong> (<code className="text-xs bg-muted px-1.5 py-0.5 rounded">shipments</code>) representa
              la orden logística completa de principio a fin. Un <strong>Viaje</strong>{" "}
              (<code className="text-xs bg-muted px-1.5 py-0.5 rounded">trips</code>) es la ejecución física asignada
              a un mobile (conductor + camión + acoplado) para ese envío.
              Ambas entidades tienen su propio campo{" "}
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">status</code> — los estados del envío
              representan la etapa logística, mientras que los estados del viaje reflejan el progreso de la ejecución
              operativa.
            </p>
          </section>

          {/* ── Estados del envío ── */}
          <section>
            <h2 className="text-base font-semibold mb-4">Estados del Envío</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Enum en DB:{" "}
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">shipment_status</code>.
              Columna:{" "}
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">shipments.status</code>.
            </p>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground w-44">Valor en DB</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground w-36">Label visible</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Significado</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground w-48">Quién lo asigna</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">en_planificacion</td>
                    <td className="px-4 py-3">En planificación</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      El envío fue creado pero aún no tiene viaje asignado ni fecha confirmada.
                      Es el estado inicial por defecto.
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <strong className="text-foreground">Admin / Operator</strong> — al crear el envío o editar manualmente.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">planificado</td>
                    <td className="px-4 py-3">Planificado</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      El envío tiene un mobile asignado y las fechas programadas están confirmadas.
                      En Batea: la card de la app muestra el botón "Confirmar viaje".
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <strong className="text-foreground">Admin / Operator</strong> — al asignar el mobile y confirmar la planificación.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">activo</td>
                    <td className="px-4 py-3">Activo</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      El chofer confirmó el viaje pero aún no salió hacia el origen.
                      En Batea: se registra cuando el driver presiona "Confirmar viaje"
                      (escribe <code className="text-xs bg-muted px-1 py-0.5 rounded">shipments.status = activo</code>).
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <strong className="text-foreground">Driver</strong> — vía{" "}
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">PATCH /api/shipments/:id</code> desde la app mobile.
                      <br />
                      <strong className="text-foreground">Admin / Operator</strong> — también puede setearlo manualmente.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">en_curso</td>
                    <td className="px-4 py-3">En curso</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      El camión partió desde el origen. En Batea: se activa cuando el driver presiona "Comenzar viaje"
                      (escribe <code className="text-xs bg-muted px-1 py-0.5 rounded">origin_departure_at</code>).
                      Al mismo tiempo el trip asociado también pasa a <em>en_curso</em>.
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <strong className="text-foreground">Driver</strong> — hito "Comenzar viaje" en la app mobile.<br />
                      <strong className="text-foreground">Admin / Operator</strong> — manualmente.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">finalizado</td>
                    <td className="px-4 py-3">Finalizado</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      La descarga en destino fue registrada. El envío está cerrado. En Batea: se activa cuando el driver
                      presiona "Descarga en destino" (escribe <code className="text-xs bg-muted px-1 py-0.5 rounded">destination_unloading_at</code>).
                      Al mismo tiempo el trip asociado pasa a <em>completado</em>.
                      Los envíos finalizados son excluidos de la lista por defecto
                      (<code className="text-xs bg-muted px-1 py-0.5 rounded">excludeFinalizado=true</code>).
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <strong className="text-foreground">Driver</strong> — hito "Descarga en destino" en la app mobile.<br />
                      <strong className="text-foreground">Admin / Operator</strong> — manualmente.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Diagrama de transiciones del envío ── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Flujo de transiciones — Envío</h2>
            <div className="rounded-md border bg-muted/30 p-4 font-mono text-xs leading-loose text-muted-foreground whitespace-pre">
{`en_planificacion  ──►  planificado  ──►  activo  ──►  en_curso  ──►  finalizado
       ▲                   ▲ │                              │
       │                   │ └── (Admin/Op puede revertir manualmente)
       └─── Cualquier estado puede volver a un anterior vía edición manual (Admin/Op) ───┘`}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              No hay validación automática de transiciones en el backend — el campo{" "}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">status</code> acepta cualquier valor del enum
              en un PATCH. La secuencia natural es la descrita arriba; los drivers están limitados a un subset de campos.
            </p>
          </section>

          {/* ── Estados del viaje ── */}
          <section>
            <h2 className="text-base font-semibold mb-4">Estados del Viaje</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Enum en DB:{" "}
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">trip_status</code>.
              Columna:{" "}
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">trips.status</code>.
            </p>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground w-32">Valor en DB</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground w-32">Label visible</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Significado</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground w-48">Quién lo asigna</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">pendiente</td>
                    <td className="px-4 py-3">Pendiente</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      El viaje fue creado y asignado a un mobile, pero el chofer aún no inició la ejecución.
                      Es el valor por defecto al crear un trip.
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <strong className="text-foreground">Admin / Operator</strong> — al crear el trip
                      (<code className="text-xs bg-muted px-1 py-0.5 rounded">POST /api/trips</code>)
                      se asigna automáticamente si no se especifica otro valor.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">en_curso</td>
                    <td className="px-4 py-3">En curso</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      El camión está en tránsito. En Batea, este estado se activa junto con el hito
                      "Comenzar viaje" del shipment (cuando el driver registra{" "}
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">origin_departure_at</code>).
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <strong className="text-foreground">Driver</strong> — vía{" "}
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">PATCH /api/trips/:id</code>.
                      Los drivers solo pueden actualizar el campo{" "}
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">status</code> en sus propios viajes.<br />
                      <strong className="text-foreground">Admin / Operator</strong> — pueden actualizar todos los campos.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">completado</td>
                    <td className="px-4 py-3">Completado</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      El viaje llegó y descargó en destino. En Batea, se activa junto con el hito
                      "Descarga en destino" del shipment (cuando el driver registra{" "}
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">destination_unloading_at</code>
                      y el shipment pasa a <em>finalizado</em>).
                      El viaje pasa a la sección "Anteriores" en la app mobile.
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <strong className="text-foreground">Driver</strong> — hito "Descarga en destino" en la app mobile.<br />
                      <strong className="text-foreground">Admin / Operator</strong> — manualmente.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">cancelado</td>
                    <td className="px-4 py-3">Cancelado</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      El viaje fue cancelado antes de completarse. El shipment asociado
                      no cambia de estado automáticamente — requiere actualización manual por parte de un Admin u Operator.
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <strong className="text-foreground">Admin / Operator</strong> — solo ellos pueden cancelar un viaje.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Relación entre los estados ── */}
          <section>
            <h2 className="text-base font-semibold mb-4">Relación entre estados — Envío ↔ Viaje</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Los cambios de estado están coordinados pero son independientes: un envío puede estar en
              cualquier estado sin que el viaje se actualice automáticamente (salvo en los hitos Batea
              descriptos abajo). La siguiente tabla muestra las combinaciones más comunes y su significado operativo.
            </p>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">shipments.status</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">trips.status</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Situación operativa</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">en_planificacion</td>
                    <td className="px-4 py-3 text-muted-foreground">— (sin trip)</td>
                    <td className="px-4 py-3 text-muted-foreground">El envío existe pero no tiene mobile asignado aún.</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">planificado</td>
                    <td className="px-4 py-3 font-mono text-xs">pendiente</td>
                    <td className="px-4 py-3 text-muted-foreground">Mobile asignado. La app mobile muestra "Confirmar viaje".</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">activo</td>
                    <td className="px-4 py-3 font-mono text-xs">pendiente</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      El chofer confirmó el viaje pero el camión aún no salió. Los hitos de origen
                      (llegada, carga) se están registrando.
                    </td>
                  </tr>
                  <tr className="bg-primary/5">
                    <td className="px-4 py-3 font-mono text-xs">en_curso</td>
                    <td className="px-4 py-3 font-mono text-xs">en_curso</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <strong className="text-foreground">Estado activo de tránsito.</strong> El camión está en ruta.
                      Ambos estados se actualizan juntos cuando el driver registra{" "}
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">origin_departure_at</code>.
                    </td>
                  </tr>
                  <tr className="bg-primary/5">
                    <td className="px-4 py-3 font-mono text-xs">finalizado</td>
                    <td className="px-4 py-3 font-mono text-xs">completado</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <strong className="text-foreground">Estado final normal.</strong> La descarga fue confirmada.
                      Ambos estados se actualizan juntos cuando el driver registra{" "}
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">destination_unloading_at</code>.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">en_curso</td>
                    <td className="px-4 py-3 font-mono text-xs">cancelado</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      El viaje fue cancelado mientras el envío ya estaba en curso. El envío debe
                      actualizarse manualmente o reasignarse a un nuevo mobile.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Hitos que sincronizan ambos estados (Batea) ── */}
          <section>
            <h2 className="text-base font-semibold mb-4">Hitos Batea que sincronizan ambos estados</h2>
            <p className="text-sm text-muted-foreground mb-4">
              En el flujo Batea de la app mobile, dos hitos provocan un doble PATCH (uno sobre el shipment
              y otro sobre el trip) que mantiene los estados sincronizados:
            </p>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Hito (botón en la app)</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Campo timestamp escrito</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">shipments.status → </th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">trips.status → </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-3 font-medium">Comenzar viaje</td>
                    <td className="px-4 py-3 font-mono text-xs">shipments.origin_departure_at</td>
                    <td className="px-4 py-3 font-mono text-xs">en_curso</td>
                    <td className="px-4 py-3 font-mono text-xs">en_curso</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Descarga en destino</td>
                    <td className="px-4 py-3 font-mono text-xs">shipments.destination_unloading_at</td>
                    <td className="px-4 py-3 font-mono text-xs">finalizado</td>
                    <td className="px-4 py-3 font-mono text-xs">completado</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Estos dos PATCHs son disparados secuencialmente desde el hook{" "}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">useBateaHandlers.ts</code> de la app mobile.
              El segundo PATCH (sobre el trip) solo se ejecuta si el primero (sobre el shipment) retornó 200.
            </p>
          </section>

          {/* ── Permisos por rol ── */}
          <section>
            <h2 className="text-base font-semibold mb-4">Permisos por rol para modificar estados</h2>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Acción</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Endpoint</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">admin</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">operator</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">fleet_manager</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">driver</th>
                  </tr>
                </thead>
                <tbody className="divide-y text-muted-foreground">
                  <tr>
                    <td className="px-4 py-3">Crear shipment</td>
                    <td className="px-4 py-3 font-mono text-xs">POST /api/shipments</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">❌</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Editar shipment (todos los campos)</td>
                    <td className="px-4 py-3 font-mono text-xs">PATCH /api/shipments/:id</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">❌</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">
                      Editar shipment (solo campos de hito){" "}
                      <span className="text-xs font-normal">status, tripPlan, timestamps de origen/destino, finalDestination</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">PATCH /api/shipments/:id</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">✅ (solo su trip)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Crear trip</td>
                    <td className="px-4 py-3 font-mono text-xs">POST /api/trips</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">❌</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Editar trip (todos los campos)</td>
                    <td className="px-4 py-3 font-mono text-xs">PATCH /api/trips/:id</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">❌</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">
                      Editar trip (solo status){" "}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">PATCH /api/trips/:id</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">✅ (solo su trip)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Eliminar / restaurar trip</td>
                    <td className="px-4 py-3 font-mono text-xs">DELETE · PATCH /restore</td>
                    <td className="px-4 py-3">✅</td>
                    <td className="px-4 py-3">❌</td>
                    <td className="px-4 py-3">❌</td>
                    <td className="px-4 py-3">❌</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              El guard para drivers valida que exista un trip vinculado al shipment a través de
              la cadena{" "}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">trips → mobiles → drivers → users</code>.
              Si el driver no tiene un trip asignado al shipment, la solicitud retorna <strong>403 Forbidden</strong>.
            </p>
          </section>

          {/* ── Impacto en exports y listas ── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Impacto de los estados en listas y exports</h2>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>
                <strong className="text-foreground">Lista de envíos (GET /api/shipments)</strong> — acepta
                el parámetro <code className="text-xs bg-muted px-1 py-0.5 rounded">status</code> para filtrar
                por estado. Además, acepta{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">excludeFinalizado=true</code> para
                excluir los envíos cerrados de la vista operativa habitual.
              </p>
              <p>
                <strong className="text-foreground">Lista de viajes (GET /api/trips)</strong> — acepta
                el parámetro <code className="text-xs bg-muted px-1 py-0.5 rounded">status</code> para filtrar
                por estado del trip. Puede combinarse con{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">shipmentId</code> para ver todos
                los trips de un envío concreto.
              </p>
              <p>
                <strong className="text-foreground">App mobile (GET /api/trips/me)</strong> — retorna todos
                los trips no eliminados del driver autenticado, incluyendo{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">shipmentStatus</code> para que la app
                pueda mostrar el estado correcto de cada hito en la card. Solo muestra viajes con{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">workLine === "batea"</code>.
              </p>
            </div>
          </section>

        </div>
      </div>
    </>
  )
}
